import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CenteredPageInner, PageContainer } from '../../components/Layout/PageContainer'
import { PageLoading } from '../../components/PageLoading'
import { BillPayStatus, VerificationStatus } from './VerificationContext'
import { useIdentityVerification } from './useIdentityVerification'
import { AwaitingVerificationCompletionCard } from './AwaitingVerificationCompletionCard'
import { GlassCard } from '../../components/Layout/GlassCard'
import { Body, Button, Form, Spacer } from '@spritz-finance/ui'
import { SubmitHandler } from 'react-hook-form'
import {
    useUpdateMethodfiSsnMutation,
    useVerificationDataQuery,
} from '../../graphql/generated/graphql'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'
import styled from 'styled-components'

type SSNFormData = {
    ssn: string
}

const FormWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    input {
        width: 300px;
    }
`

interface SSNFormProps {
    status: BillPayStatus
}

const SSNForm: React.VFC<SSNFormProps> = ({ status }) => {
    const [updateSSN, { loading }] = useUpdateMethodfiSsnMutation()
    const navigate = useNavigate()
    const [SSN4Error, setSSN4Error] = useState(false)
    const [SSN9Error, setSSN9Error] = useState(false)
    const { refetch } = useVerificationDataQuery()

    const isSSN4 = status === BillPayStatus.NeedsSSN4

    const onSubmit: SubmitHandler<SSNFormData> = async ({ ssn }) => {
        setSSN4Error(false)
        setSSN9Error(false)

        await updateSSN({
            variables: {
                ssn,
            },
        })
            .then(async () => {
                await refetch()
                navigate('/bills/main')
            })
            .catch(async () => {
                if (isSSN4) {
                    setSSN4Error(true)
                } else {
                    setSSN9Error(true)
                }

                await refetch()
            })
    }

    const text = isSSN4
        ? 'the last 4 digits of your Social Security Number '
        : 'your 9 digit Social Security Number '

    return (
        <div>
            <h2>Bill Pay Verification</h2>
            <Body.Med>
                We had trouble fully syncing your identity with our bill provider. Please provide{' '}
                {text}
                in order to access our bill pay functionality:
            </Body.Med>

            <Form<SSNFormData> onSubmit={onSubmit} width="auto">
                <FormWrapper>
                    <Spacer height={16} />
                    <Form.Input
                        name="ssn"
                        placeholder={isSSN4 ? 'Last 4 digits of SSN' : '9 digit SSN'}
                        validation={{
                            required: true,
                            validate: {
                                validSSN: (_ssn) => {
                                    const ssn = _ssn.replaceAll(/[\- ]/g, '')
                                    const ssnRegex = isSSN4 ? /^\d{4}$/ : /^\d{9}$/
                                    if (!ssn.match(ssnRegex)) {
                                        return 'Invalid SSN'
                                    }
                                },
                            },
                        }}
                    />

                    <Button.CTA type="submit" loading={loading}>
                        Submit
                    </Button.CTA>
                    <Spacer height={16} />
                    <Button.CTA variant="secondary" onClick={() => navigate('/bills/main')}>
                        Go Back
                    </Button.CTA>

                    {SSN4Error && (
                        <>
                            <Spacer height={16} />
                            <ErrorMessage message="Error verifying your identity with the last 4 digits of your SSN! Please enter your full number above and resubmit" />
                        </>
                    )}
                    {SSN9Error && (
                        <>
                            <Spacer height={16} />
                            <ErrorMessage message="Error verifying your identity with your full SSN! Please contact support." />
                        </>
                    )}
                </FormWrapper>
            </Form>
        </div>
    )
}

const Continue = () => {
    const { startVerification } = useIdentityVerification()

    return (
        <div>
            <Body.Bold size={24}>Verification Incomplete</Body.Bold>
            <Spacer height={24} />
            <Body.Med size={12}>Please click to finish verifying your identity.</Body.Med>
            <Spacer height={24} />
            <Button.CTA onClick={startVerification}>Resume Verification</Button.CTA>
        </div>
    )
}

export const VerificationPage: React.VFC = () => {
    const {
        verificationStatus,
        billPayStatus,
        startVerification,
        loading,
        flowLoading,
        flowClosed,
    } = useIdentityVerification()

    const navigate = useNavigate()

    useEffect(() => {
        if (
            billPayStatus === BillPayStatus.NeedsSSN4 ||
            billPayStatus === BillPayStatus.NeedsSSN9
        ) {
            return
        }

        if (
            verificationStatus === VerificationStatus.Failed ||
            (verificationStatus === VerificationStatus.Verified &&
                billPayStatus !== BillPayStatus.Waiting)
        ) {
            navigate('/bills/main')
        }
        if (
            verificationStatus === VerificationStatus.Active ||
            verificationStatus === VerificationStatus.NotStarted
        ) {
            startVerification()
        }
    }, [verificationStatus, billPayStatus, startVerification])

    if (loading || flowLoading) return <PageLoading />

    let content: any = null
    if (
        verificationStatus === VerificationStatus.UserCompleted ||
        (verificationStatus === VerificationStatus.Verified &&
            billPayStatus === BillPayStatus.Waiting)
    ) {
        content = (
            <GlassCard>
                <AwaitingVerificationCompletionCard />
            </GlassCard>
        )
    }

    if (billPayStatus === BillPayStatus.NeedsSSN4 || billPayStatus === BillPayStatus.NeedsSSN9) {
        content = (
            <GlassCard>
                <SSNForm status={billPayStatus} />
            </GlassCard>
        )
    }

    if (
        (verificationStatus === VerificationStatus.Active ||
            verificationStatus === VerificationStatus.NotStarted) &&
        flowClosed
    ) {
        content = (
            <GlassCard>
                <Continue />
            </GlassCard>
        )
    }

    return (
        <PageContainer>
            <CenteredPageInner $width={700}>{content}</CenteredPageInner>
        </PageContainer>
    )
}
